import type { SyntheticEvent } from "react"
import type { FieldValues, UseFormProps } from "react-hook-form"
import type { z } from "zod"

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"

export function useValidatedForm<
  Schema extends z.Schema<any, any>,
  InputFieldValues extends FieldValues = z.input<Schema>,
  OutputFieldValues extends FieldValues = z.output<Schema>,
  InputFieldValuesProps extends InputFieldValues = InputFieldValues, // An extra generic is used for Props, because then, defaultValues cannot influence the base schema and are independent
>({
  validation,
  defaultValues,
  mode = "onTouched",
  onSubmit,
  ...restFormOptions
}: {
  validation: Schema
  onSubmit: (data: OutputFieldValues, e: SyntheticEvent<HTMLFormElement, SubmitEvent>) => void | Promise<void>
} & Omit<UseFormProps<InputFieldValuesProps>, "resolver" | "formControl">) {
  const { register, formState, handleSubmit, ...restReturn } = useForm<InputFieldValues, any, OutputFieldValues>({
    ...restFormOptions,
    mode: mode,
    defaultValues: defaultValues,
    resolver: zodResolver(validation),
  })

  /**
   * Executes the handleSubmit function of react-hook-forms and wraps it with a custom callback
   */
  function onSubmitWrapper(onSubmit: (data: OutputFieldValues, e: SyntheticEvent<HTMLFormElement, SubmitEvent>) => void) {
    return handleSubmit(
      (async (data: OutputFieldValues, e: any) => {
        // Remove confirmation signs from the inputs
        setDataTouched(e!.target as unknown as HTMLElement[], "false")

        await onSubmit(data, e)
      }) as any,
      (errors, e) => {
        // Set inputs to touched, so the confirmation signs can rerender
        setDataTouched(e!.target as unknown as HTMLElement[], "true")
      },
    )
  }

  return { register, formState, onSubmit: onSubmitWrapper(onSubmit), validationSchema: validation, ...restReturn }
}

export function setDataTouched(inputs: HTMLElement[], value: string, includesName?: string) {
  for (const formElement of inputs) {
    if (formElement.getAttribute("data-touched") && (!includesName || formElement.getAttribute("name")?.includes(includesName))) {
      formElement.setAttribute("data-touched", value)
    }
  }
}
